import { useEffect, useState } from "react";

/**
 * Fired by navigateToBaseRoute so that useRouteParams can pick up a pathname
 * change made via history.pushState (which does not fire popstate).
 */
const MODULE_NAV_EVENT = "fakhri:module-navigate";

export function parsePathname(pathname: string = window.location.pathname): {
  baseRoute: string | null;
  routeId: string | null;
} {
  const segments = pathname
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean);

  if (segments.length === 0) {
    return { baseRoute: null, routeId: null };
  }

  const baseRoute = segments[0];
  const routeId = segments.length >= 2 ? segments[1] : null;

  return { baseRoute, routeId };
}

export function parseHash(): string[] {
  const hash = window.location.hash.replace(/^#+/, "");
  return hash.split("/").filter(Boolean);
}

export function getBaseRouteFromPathname(
  pathname: string = window.location.pathname
): string | null {
  const { baseRoute } = parsePathname(pathname);
  return baseRoute;
}

export function getRouteIdFromPathname(pathname: string = window.location.pathname): string | null {
  const { routeId } = parsePathname(pathname);
  return routeId;
}

export function navigateToBaseRoute(
  baseRoute: string | null,
  routeId: string | null = null,
  hashPaths: string[] = []
): void {
  let pathname = "/";

  if (baseRoute) {
    const normalizedBase = baseRoute.replace(/^\/+|\/+$/g, "");
    pathname = `/${normalizedBase}/`;
    if (routeId) {
      pathname += `${routeId}/`;
    }
  }

  let hash = "#/";
  if (hashPaths && hashPaths.length > 0) {
    const cleanPaths = hashPaths.filter((p) => p !== "").map((p) => p.replace(/^\/+|\/+$/g, ""));
    if (cleanPaths.length > 0) {
      hash = `#/${cleanPaths.join("/")}/`;
    }
  }

  const newUrl = pathname + hash;

  // Use pushState so that module-to-module navigation is a smooth client-side
  // transition instead of a full page reload.
  //
  // After pushState we must dispatch a synthetic popstate so that react-router's
  // createHashRouter (which listens to popstate, not hashchange) updates its
  // internal location to the new hash. Without this, the hash router keeps its
  // stale location and renders "Not Found" until the page is refreshed.
  //
  // The custom MODULE_NAV_EVENT lets useRouteParams detect the pathname change
  // (popstate only fires for back/forward, not pushState).
  window.history.pushState(null, "", newUrl);
  window.dispatchEvent(new PopStateEvent("popstate", { state: null }));
  window.dispatchEvent(new Event(MODULE_NAV_EVENT));
}

/** Full URL for the bookings module hall-booking show (`/bookings/#/bookings/:id/show/`). */
export function buildBookingShowUrl(bookingId: string): string {
  const id = String(bookingId).replace(/^\/+|\/+$/g, "");
  return `${window.location.origin}/bookings/#/bookings/${id}/show/`;
}

/** Full URL for sadarat create in the current module (`/bookings/#/sadarats/create`). */
export function buildSadaratCreateUrl(
  origin: string = typeof window !== "undefined" ? window.location.origin : "",
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "/"
): string {
  const { baseRoute } = parsePathname(pathname);
  const modulePath = baseRoute ? `/${baseRoute}/` : "/";
  return `${origin}${modulePath}#/sadarats/create`;
}

export function useRouteParams(): { baseRoute: string | null; routeId: string | null } {
  const [params, setParams] = useState(() => parsePathname());

  useEffect(() => {
    const update = () => {
      const next = parsePathname();
      // Only update state (and trigger a re-render) when the pathname-derived values
      // actually change. Returning the previous reference on a no-op prevents
      // react-admin's internal hash router from being recreated on every back/forward
      // navigation, which would corrupt browser history state and break the back button.
      setParams((prev) =>
        prev.baseRoute === next.baseRoute && prev.routeId === next.routeId ? prev : next
      );
    };

    // popstate  — browser back / forward
    // MODULE_NAV_EVENT — our own pushState-based module navigation (pushState
    //   never fires popstate, so we dispatch a custom event from navigateToBaseRoute)
    window.addEventListener("popstate", update);
    window.addEventListener(MODULE_NAV_EVENT, update);

    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener(MODULE_NAV_EVENT, update);
    };
  }, []);

  return params;
}

export function useBaseRoute(): string | null {
  const { baseRoute } = useRouteParams();
  return baseRoute;
}

export function useRouteId(): string | null {
  const { routeId } = useRouteParams();
  return routeId;
}
